/**
 * Carrier Portal · Public (no-auth) surface reached via signed handoff token.
 *
 * Route: /carrier/:token
 * Backend: GET /api/dispatch/handoff/{token}, POST /api/dispatch/handoff/{token}/pop
 *
 * Visual identity: Stockaris branding but with a prominent "EXTERNAL CARRIER
 * VIEW" amber banner so internal ops don't mistake it for the main app.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import OfflineIndicator from '../components/OfflineIndicator';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PWAInstallButton from '../components/PWAInstallButton';
import {
  Truck, ShieldCheck, Camera, MapPin, PencilSimple, Warning,
  CheckCircle, ClockCountdown, SealCheck, Package,
} from '@phosphor-icons/react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tiny draw-pad that writes base64 PNG signatures. Inline to avoid new deps.
const SignaturePad = ({ onChange, disabled }) => {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
  }, []);

  const point = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const scaleX = c.width / r.width;
    const scaleY = c.height / r.height;
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
    return { x: x * scaleX, y: y * scaleY };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    setDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    const p = point(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = point(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setDirty(true);
  };
  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    if (onChange) onChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, c.width, c.height);
    setDirty(false);
    if (onChange) onChange('');
  };

  return (
    <div className="border border-border bg-card">
      <canvas
        ref={canvasRef}
        width={640}
        height={180}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="w-full block touch-none cursor-crosshair"
        data-testid="carrier-signature-pad"
        style={{ maxWidth: '100%' }}
      />
      <div className="flex justify-between items-center px-2 py-1 border-t border-border text-xs">
        <span className={`font-mono ${dirty ? 'text-emerald-600' : 'text-muted-foreground'}`}>
          {dirty ? '✓ captured' : 'sign above'}
        </span>
        <button type="button" onClick={clear} disabled={disabled || !dirty}
          data-testid="carrier-signature-clear"
          className="text-rose-600 hover:text-rose-700 disabled:text-slate-300 text-xs font-bold uppercase tracking-widest">
          Clear
        </button>
      </div>
    </div>
  );
};

export default function CarrierPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // POP form state
  const [signerName, setSignerName] = useState('');
  const [signature, setSignature] = useState('');
  const [gps, setGps] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [photo, setPhoto] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/dispatch/handoff/${token}`);
        setData(res.data);
      } catch (e) {
        const msg = e?.response?.data?.detail || 'Unable to load dispatch';
        setError(msg);
      } finally { setLoading(false); }
    })();
  }, [token]);

  const captureGPS = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported by this browser');
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy });
        setGpsLoading(false);
      },
      (err) => {
        toast.error(`GPS failed: ${err.message || 'permission denied'}`);
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) {
      toast.error('Photo too large (max 6MB)');
      return;
    }
    const r = new FileReader();
    r.onload = () => setPhoto(r.result);
    r.readAsDataURL(f);
  };

  const submitPOP = async () => {
    if (!signerName.trim() || signerName.trim().length < 2) {
      toast.error('Please enter your name');
      return;
    }
    if (!signature) {
      toast.error('Please sign the pad above');
      return;
    }
    setSubmitting(true);
    const payload = {
      signer_name: signerName.trim(),
      signature_base64: signature,
      gps_lat: gps?.lat,
      gps_lng: gps?.lng,
      gps_accuracy_m: gps?.acc,
      photo_base64: photo || null,
      notes: notes.trim() || null,
      device_info: navigator.userAgent?.slice(0, 180),
    };
    const path = `/api/dispatch/handoff/${token}/pop`;
    // Offline fast-path — queue the POP so drivers can confirm pickup
    // even without signal. Queue replays automatically when back online.
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      try {
        const mod = await import('../lib/offlineQueue');
        mod.enqueue({ path, method: 'POST', body: payload, label: `POP · ${String(token).slice(0, 8)}` });
        toast.success('Offline — pickup queued. Will sync when back online.');
        // Optimistic: mark POP submitted locally so UI updates
        setData((d) => d ? { ...d, pop_submitted: true } : d);
      } finally {
        setSubmitting(false);
      }
      return;
    }
    try {
      await axios.post(`${API_BASE}${path.replace('/api', '')}`, payload);
      toast.success('Proof of pickup captured');
      // Reload snapshot so POP section updates
      const res = await axios.get(`${API_BASE}/dispatch/handoff/${token}`);
      setData(res.data);
    } catch (e) {
      if (!navigator.onLine || e?.message === 'Network Error') {
        const mod = await import('../lib/offlineQueue');
        mod.enqueue({ path, method: 'POST', body: payload, label: `POP · ${String(token).slice(0, 8)}` });
        toast.success('Connection lost — pickup queued. Will sync when back online.');
        setData((d) => d ? { ...d, pop_submitted: true } : d);
      } else {
        toast.error(e?.response?.data?.detail || 'Submission failed');
      }
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <p className="text-muted-foreground font-mono text-sm">Loading dispatch…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-card border border-rose-300 p-6 text-center" data-testid="carrier-error">
          <Warning size={40} weight="duotone" className="text-rose-600 mx-auto mb-3" />
          <h1 className="text-lg font-bold tracking-tight mb-1">Link unavailable</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <p className="text-xs text-muted-foreground mt-4 font-mono">
            Token: {String(token).slice(0, 8)}…
          </p>
        </div>
      </div>
    );
  }

  const alreadySubmitted = data?.pop_submitted;

  return (
    <div className="min-h-screen bg-muted" data-testid="carrier-portal-page">
      <Toaster position="top-center" richColors />
      <OfflineIndicator />

      {/* External banner */}
      <div className="bg-amber-400 text-amber-950 text-center py-1.5 px-4 text-xs font-bold uppercase tracking-widest border-b-2 border-amber-500"
        data-testid="carrier-external-banner">
        <ShieldCheck size={14} weight="bold" className="inline mr-1" />
        External Carrier View · Signed link
      </div>

      {/* Header */}
      <header className="bg-brand text-white border-b-4 border-amber-400">
        <div className="max-w-4xl mx-auto px-4 py-5 flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] opacity-70">Stockaris WMS</p>
            <h1 className="text-2xl font-bold">Carrier Pickup Portal</h1>
          </div>
          <div className="flex items-center gap-3">
            <div data-testid="carrier-pwa-install" className="bg-card/20 hover:bg-card/30 transition rounded-none">
              <PWAInstallButton compact />
            </div>
            <Truck size={40} weight="fill" className="opacity-90 hidden md:block" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 space-y-5">
        {/* Dispatch summary */}
        <div className="bg-card border border-border rounded-lg p-4" data-testid="carrier-dispatch-summary">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="overline text-muted-foreground">Dispatch</p>
              <h2 className="text-xl font-bold font-mono tracking-tight">{data?.dispatch_number || '—'}</h2>
            </div>
            <span className="px-3 py-1 border border-border font-mono text-xs uppercase tracking-widest bg-muted" data-testid="carrier-status">
              {data?.status?.replace('_', ' ')}
            </span>
          </div>
          <dl className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <Info label="Customer" value={data?.customer_name} />
            <Info label="Destination" value={data?.destination} />
            <Info label="Carrier" value={data?.carrier || data?.transport_company} />
            <Info label="Container" value={data?.container_number} mono />
            <Info label="Container Type" value={data?.container_type} mono />
            <Info label="Trailer" value={data?.trailer_rego} mono />
            <Info label="Driver" value={data?.driver_name} />
            <Info label="Planned pickup" value={fmt(data?.planned_dispatch_at)} />
            <Info label="Seals" value={(data?.seal_numbers || []).join(', ')} mono />
          </dl>
          {data?.totals && (
            <div className="mt-3 pt-3 border-t border-border grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Info label="Units loaded" value={`${data.totals.total_loaded_units} / ${data.totals.total_allocated_units}`} mono />
              <Info label="Weight" value={`${data.totals.total_weight_kg} / ${data.totals.max_weight_kg} kg`} mono />
              <Info label="Volume" value={`${data.totals.total_volume_m3} / ${data.totals.max_volume_m3} m³`} mono />
              <Info label="Utilisation" value={`${Math.max(data.totals.weight_pct, data.totals.volume_pct)}%`} mono />
            </div>
          )}
        </div>

        {/* Allocations */}
        {(data?.allocations || []).length > 0 && (
          <div className="bg-card border border-border rounded-lg" data-testid="carrier-allocations">
            <div className="px-4 py-2 border-b border-border flex items-center gap-2">
              <Package size={16} weight="bold" />
              <h3 className="text-sm font-bold tracking-tight uppercase">Allocated Orders ({data.allocations.length})</h3>
            </div>
            <div className="divide-y divide-slate-100">
              {data.allocations.map((a, i) => (
                <div key={i} className="px-4 py-3 flex flex-col md:flex-row md:justify-between gap-2">
                  <div>
                    <p className="text-xs font-mono font-bold">{a.order_number || a.order_id?.slice(-8)}</p>
                    <p className="text-xs text-muted-foreground">{a.customer_name}</p>
                  </div>
                  <div className="text-xs font-mono text-muted-foreground">
                    {(a.items || []).reduce((acc, it) => acc + (it.qty_allocated || 0), 0)} units ·{' '}
                    {(a.items || []).reduce((acc, it) => acc + (it.qty_loaded || 0), 0)} loaded
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* POP capture or receipt */}
        {alreadySubmitted ? (
          <div className="bg-emerald-50 border border-emerald-300 p-5" data-testid="carrier-pop-receipt">
            <div className="flex items-center gap-3">
              <CheckCircle size={28} weight="fill" className="text-emerald-600" />
              <div>
                <h3 className="text-lg font-bold tracking-tight">Proof of Pickup captured</h3>
                <p className="text-sm text-muted-foreground">Thank you, {data?.pop?.signer_name}. This dispatch is complete from your side.</p>
                {data?.pop?.signed_at && (
                  <p className="text-xs font-mono text-muted-foreground mt-1">
                    Signed {new Date(data.pop.signed_at).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            {data?.pop?.signature_base64 && (
              <div className="mt-4 bg-card border border-border rounded-lg p-3 inline-block">
                <img src={data.pop.signature_base64} alt="Signature" style={{ maxHeight: 120 }} />
              </div>
            )}
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg" data-testid="carrier-pop-form">
            <div className="px-4 py-2 border-b border-border flex items-center gap-2 bg-brand text-white">
              <SealCheck size={16} weight="bold" />
              <h3 className="text-sm font-bold tracking-tight uppercase">Confirm Pickup (Proof of Pickup)</h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <Label className="overline text-muted-foreground" htmlFor="signer">Full name</Label>
                <Input id="signer" value={signerName} onChange={(e) => setSignerName(e.target.value)}
                  placeholder="e.g., John Driver" maxLength={120} data-testid="carrier-signer-name"
                  className="rounded-none" />
              </div>

              <div>
                <Label className="overline text-muted-foreground flex items-center gap-1">
                  <PencilSimple size={12} weight="bold" /> Signature
                </Label>
                <SignaturePad onChange={setSignature} />
              </div>

              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1">
                  <Label className="overline text-muted-foreground flex items-center gap-1">
                    <MapPin size={12} weight="bold" /> GPS location
                  </Label>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" onClick={captureGPS} disabled={gpsLoading}
                      data-testid="carrier-capture-gps"
                      variant="outline" className="rounded-none h-[38px]">
                      {gpsLoading ? 'Locating…' : gps ? 'Re-capture' : 'Capture GPS'}
                    </Button>
                    {gps && (
                      <span className="flex-1 px-2 py-2 border border-emerald-300 bg-emerald-50 text-xs font-mono text-emerald-800"
                        data-testid="carrier-gps-value">
                        {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)} · ±{Math.round(gps.acc)}m
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex-1">
                  <Label className="overline text-muted-foreground flex items-center gap-1" htmlFor="photo">
                    <Camera size={12} weight="bold" /> Photo of load (optional)
                  </Label>
                  <Input id="photo" type="file" accept="image/*" capture="environment" onChange={onPhoto}
                    data-testid="carrier-photo-input"
                    className="rounded-none h-[38px]" />
                  {photo && <p className="text-[10px] text-emerald-600 mt-1 font-mono">✓ photo ready</p>}
                </div>
              </div>

              <div>
                <Label className="overline text-muted-foreground" htmlFor="notes">Notes (optional)</Label>
                <textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                  rows={2} maxLength={500}
                  data-testid="carrier-notes"
                  className="w-full px-3 py-2 border border-border rounded-none text-sm" />
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-border">
                <p className="text-[10px] text-muted-foreground font-mono flex items-center gap-1">
                  <ClockCountdown size={12} weight="bold" />
                  {data?.token_info?.expires_at && `Link expires ${new Date(data.token_info.expires_at).toLocaleString()}`}
                </p>
                <Button
                  onClick={submitPOP}
                  disabled={submitting || !signerName.trim() || !signature}
                  data-testid="carrier-submit-pop"
                  className="bg-brand text-white rounded-none hover:bg-[#0040d9] disabled:opacity-50"
                >
                  {submitting ? 'Submitting…' : 'Confirm Pickup'}
                </Button>
              </div>
            </div>
          </div>
        )}

        <footer className="text-center text-[10px] text-muted-foreground font-mono pt-4 pb-8">
          Powered by Stockaris WMS · This link is audit-logged.
        </footer>
      </main>
    </div>
  );
}

const Info = ({ label, value, mono }) => (
  <div>
    <dt className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{label}</dt>
    <dd className={`text-sm text-slate-800 ${mono ? 'font-mono' : ''}`}>{value || '—'}</dd>
  </div>
);

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—');
